import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { ActiveAlert } from '../types/savedSearch.types';
import savedSearchService from '../services/savedSearch.service';
import { useAuth } from '../contexts/AuthContext';

/**
 * Source unique des alertes (partagée Commandes / Dashboard / bouton mobile).
 * - Snapshot persisté en localStorage : affichage immédiat après rechargement,
 *   puis revalidation réseau en arrière-plan (stale-while-revalidate).
 * - Les valeurs en mémoire survivent aux changements de vue et aux remontages.
 * - Rafraîchissement dédupliqué + déclenché au montage, au retour d'onglet,
 *   à la reconnexion réseau, périodiquement et via les événements temps réel.
 */

interface Entry {
  alerts: ActiveAlert[];
  fetchedAt: number;
  hydrated: boolean;
}

const EMPTY: ActiveAlert[] = [];
const STALE_AFTER_MS = 30_000;
const MIN_REFRESH_GAP_MS = 1_500;
const POLL_INTERVAL_MS = 60_000;
const STORAGE_PREFIX = 'alerts_snapshot_v1:';

const entries = new Map<string, Entry>();
const listeners = new Map<string, Set<() => void>>();
const inFlight = new Map<string, Promise<void>>();

const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`;

const sameAlerts = (a: ActiveAlert[], b: ActiveAlert[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i];
    const y = b[i];
    if (
      x.searchId !== y.searchId ||
      x.count !== y.count ||
      x.type !== y.type ||
      x.searchName !== y.searchName ||
      x.searchIcon !== y.searchIcon
    ) {
      return false;
    }
  }
  return true;
};

const hydrate = (key: string): Entry => {
  const existing = entries.get(key);
  if (existing) return existing;
  let entry: Entry = { alerts: EMPTY, fetchedAt: 0, hydrated: true };
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.alerts)) {
        entry = { alerts: parsed.alerts, fetchedAt: 0, hydrated: true };
      }
    }
  } catch {
    // snapshot illisible : on repart de zéro
  }
  entries.set(key, entry);
  return entry;
};

const emit = (key: string) => {
  listeners.get(key)?.forEach((fn) => fn());
};

const write = (key: string, alerts: ActiveAlert[], fetchedAt: number) => {
  const current = hydrate(key);
  const changed = !sameAlerts(current.alerts, alerts);
  entries.set(key, {
    alerts: changed ? alerts : current.alerts,
    fetchedAt,
    hydrated: true,
  });
  if (changed) {
    try {
      localStorage.setItem(storageKey(key), JSON.stringify({ alerts }));
    } catch {
      // quota : non bloquant
    }
    emit(key);
  }
};

export const alertsStore = {
  get(key: string): ActiveAlert[] {
    return hydrate(key).alerts;
  },

  subscribe(key: string, fn: () => void) {
    let set = listeners.get(key);
    if (!set) {
      set = new Set();
      listeners.set(key, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
    };
  },

  /** Alignement depuis une source externe déjà à jour (ex. dashboard). */
  publish(key: string, alerts: ActiveAlert[]) {
    write(key, alerts, Date.now());
  },

  /** Mise à jour instantanée des compteurs reçus en temps réel (avant confirmation réseau). */
  patchCounts(key: string, counts: Record<number, number>) {
    const current = hydrate(key);
    let touched = false;
    const next = current.alerts.map((alert) => {
      const count = counts[alert.searchId];
      if (typeof count === 'number' && count !== alert.count) {
        touched = true;
        return { ...alert, count };
      }
      return alert;
    });
    if (touched) write(key, next, current.fetchedAt);
  },

  refresh(key: string, storeId: number, opts: { force?: boolean } = {}): Promise<void> {
    const current = hydrate(key);
    const age = Date.now() - current.fetchedAt;
    if (!opts.force && age < STALE_AFTER_MS) return Promise.resolve();

    const pending = inFlight.get(key);
    if (pending) return pending;
    if (age < MIN_REFRESH_GAP_MS) return Promise.resolve();

    const fetchAlerts = async (): Promise<ActiveAlert[] | null> => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const data = await savedSearchService.getActiveAlerts(storeId, true);
          if (Array.isArray(data)) {
            // Une liste vide après des alertes peut être transitoire (recomptage serveur,
            // reprise réseau) : on la confirme avant de masquer le bouton.
            if (data.length === 0 && hydrate(key).alerts.length > 0 && attempt < 2) {
              await new Promise((resolve) => setTimeout(resolve, 1500));
              continue;
            }
            return data;
          }
        } catch (err) {
          console.error('[alertsStore] refresh failed:', err);
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
      }
      return null;
    };

    const request = fetchAlerts()
      .then((data) => {
        if (data) write(key, data, Date.now());
      })
      .finally(() => {
        inFlight.delete(key);
      });
    inFlight.set(key, request);
    return request;
  },

  isStale(key: string) {
    return Date.now() - hydrate(key).fetchedAt >= STALE_AFTER_MS;
  },
};

export function useStoreAlerts(storeId: number | undefined) {
  const { user } = useAuth();
  const key = storeId && user?.id ? `${storeId}:${user.id}` : '';
  const keyRef = useRef(key);
  keyRef.current = key;

  const alerts = useSyncExternalStore(
    useCallback((fn) => (key ? alertsStore.subscribe(key, fn) : () => undefined), [key]),
    () => (key ? alertsStore.get(key) : EMPTY),
    () => EMPTY
  );

  const refresh = useCallback(
    (force = true) => {
      if (!key || !storeId) return Promise.resolve();
      return alertsStore.refresh(key, storeId, { force });
    },
    [key, storeId]
  );

  const patchCounts = useCallback(
    (counts: Record<number, number>) => {
      if (key) alertsStore.patchCounts(key, counts);
    },
    [key]
  );

  const publish = useCallback(
    (next: ActiveAlert[]) => {
      if (key) alertsStore.publish(key, next);
    },
    [key]
  );

  useEffect(() => {
    if (!key || !storeId) return undefined;
    refresh(false);

    const revalidate = () => {
      if (document.visibilityState === 'visible') refresh(false);
    };
    document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    window.addEventListener('online', revalidate);
    window.addEventListener('pageshow', revalidate);
    const timer = window.setInterval(revalidate, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
      window.removeEventListener('online', revalidate);
      window.removeEventListener('pageshow', revalidate);
      window.clearInterval(timer);
    };
  }, [key, storeId, refresh]);

  return { alerts, refresh, patchCounts, publish };
}
